import { ComponentType, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  Divider,
  Sidebar,
  SidebarButton,
  useDisclosure,
  useResponsive,
  useToast,
} from '@bitrise/bitkit';
import { useResizeObserver } from 'usehooks-ts';
import useBuildCacheSubscriptionRequest from '@/components/BuildPerformance/hooks/useBuildCacheSubscriptionRequest';
import RequestSubscriptionDialog from '@/pages/BuildCache/BuildCacheActivation/RequestSubscriptionDialog';
import InternalFooter from '@/components/InternalFooter/InternalFooter';
import AnnouncementBanner from '@/components/banners/AnnouncementBanner/AnnouncementBanner';
import Page, { PageProps } from '@/components/page/Page';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';
import TimeBasedTrialBanner from '@/components/TimeBasedTrial/TimeBasedTrialBanner/TimeBasedTrialBanner';
import TimeBasedTrialDialog from '@/components/TimeBasedTrial/TimeBasedTrialDialog/TimeBasedTrialDialog';
import TrialUpdateDialog from '@/components/TimeBasedTrial/TrialUpdateDialog/TrialUpdateDialog';
import PlanDowngradeDialog from '@/components/PlanDowngrade/PlanDowngradeDialog/PlanDowngradeDialog';
import PlanDowngradeBanner from '@/components/PlanDowngrade/PlanDowngradeBanner/PlanDowngradeBanner';
import useActivateBuildCacheSubscriptionEvent from '@/components/BuildPerformance/hooks/useActivateBuildCacheSubscriptionEvent';
import { useShowBuildCacheTrial } from '@/components/BuildPerformance/hooks/useShowBuildCacheTrial';
import { GlobalProps, PopupAlert } from '@/models/GlobalProps';
import DynamicBanners from '@/components/banners/DynamicBanners/DynamicBanners';
import BuildCacheTrialNotification from '../BuildPerformance/BuildCacheTrialNotification';
import DeactivateBuildCacheDialog from '../BuildPerformance/DeactivateBuildCacheDialog';
import PlatformShell from './PlatformShell/PlatformShell';
import { GlobalPropsContext } from './GlobalPropsContext';

export interface ProtectedPageProps extends PageProps {
  BannerComponent?: ReactNode;
  sidebarContent?: ReactNode;
  sidebarTitle?: string;
  sidebarWidth?: string;
  footerBackground?: BoxProps['bg'];
  colorScheme?: 'white' | 'grey';
  withoutFooter?: boolean;
}

const useToastFlash = (flash: GlobalProps['flash'], onMessageDisplayed: VoidFunction) => {
  const toast = useToast();

  useEffect(() => {
    const successToast = (t: string | PopupAlert) =>
      toast({
        title: typeof t === 'string' ? 'Success' : t.title,
        description: typeof t === 'string' ? t : t.description,
        status: 'success',
        duration: 5_000,
        isClosable: true,
      });
    const errorToast = (t: string | PopupAlert) =>
      toast({
        title: typeof t === 'string' ? 'Error' : t.title,
        description: typeof t === 'string' ? t : t.description,
        status: 'error',
        duration: 10_000,
        isClosable: true,
      });

    if (flash?.notice) {
      successToast(flash.notice);
    }
    if (flash?.popupAlertSuccess) {
      successToast(flash.popupAlertSuccess);
    }

    if (flash?.alert) {
      errorToast(flash.alert);
    }
    if (flash?.popupAlertError) {
      errorToast(flash.popupAlertError);
    }
    onMessageDisplayed();
  }, [flash?.alert, flash?.notice, flash?.popupAlertError, flash?.popupAlertSuccess, onMessageDisplayed, toast]);
};

const ProtectedPage = ({
  footerBackground,
  children,
  BannerComponent,
  sidebarContent,
  sidebarTitle = '',
  sidebarWidth = '16rem',
  colorScheme = 'white',
  bg,
  withoutFooter,
  ...rest
}: ProtectedPageProps) => {
  const {
    flash,
    announcementBanner,
    account,
    featureFlags: { account: accountFeatureFlags },
  } = useContext(GlobalPropsContext);
  const [flashMessage, setFlashMessage] = useState(flash);

  useToastFlash(flashMessage, () => setFlashMessage(undefined));

  const { isMobile } = useResponsive();

  const { submit: requestSubscription } = useBuildCacheSubscriptionRequest(
    account?.slug || '',
    'build_cache_deactivation_dialog',
    () => {
      trackActivateBuildCacheSubscription(account?.slug || '');
      window.location.reload();
    },
  );

  const { submit: trackActivateBuildCacheSubscription } = useActivateBuildCacheSubscriptionEvent();

  const { isOpen: isSidebarOpen, onOpen: openSidebar } = useDisclosure();
  const footerBg = colorScheme === 'grey' ? 'neutral.100' : footerBackground;
  const pageBg = colorScheme === 'grey' ? 'neutral.95' : bg;

  const {
    isOpen: isRequestSubscriptionDialogOpen,
    onOpen: onRequestSubscriptionDialogOpen,
    onClose: onRequestSubscriptionDialogClose,
  } = useDisclosure();

  const { shouldShowBanner: shouldShowBuildCacheTrialNotification, onClose: onBuildCacheTrialNotificationClosed } =
    useShowBuildCacheTrial({
      trial: account?.buildCacheTrial ?? null,
      subscriptionRequest: account?.buildCacheSubscriptionRequest ?? null,
      hasBuildCacheSubscription: !!account?.hasBuildCacheSubscription,
      canActivateBuildCacheSubscription: !!account?.canActivateBuildCacheSubscription,
    });
  const [isBuildCacheTrialNotificationOpen, setIsBuildCacheTrialNotificationOpen] = useState(
    shouldShowBuildCacheTrialNotification,
  );

  const getBanner = (): ReactNode | undefined => {
    if (isBuildCacheTrialNotificationOpen && account?.buildCacheTrial) {
      return (
        <>
          <BuildCacheTrialNotification
            as="Ribbon"
            trialExpiresAt={account.buildCacheTrial.expiresAt}
            onActivateSubscriptionSelected={onRequestSubscriptionDialogOpen}
            onClose={(isFinalRibbon, cookieExpiresAt) => {
              if (isFinalRibbon) {
                setIsDeactivateBuildCacheDialogOpen(true);
              } else {
                setIsBuildCacheTrialNotificationOpen(false);
                onBuildCacheTrialNotificationClosed(cookieExpiresAt);
              }
            }}
          />
          <RequestSubscriptionDialog
            isOpen={isRequestSubscriptionDialogOpen}
            onClose={onRequestSubscriptionDialogClose}
            source="global_header_banner"
          />
        </>
      );
    }

    if (account?.onTimeBasedTrial) {
      return <TimeBasedTrialBanner />;
    }

    if (account?.downgradeNotifications?.banner) {
      return <PlanDowngradeBanner />;
    }

    if (announcementBanner) {
      return <AnnouncementBanner {...announcementBanner} />;
    }

    if (accountFeatureFlags?.enforceInfraBudget) {
      return <DynamicBanners />;
    }

    return undefined;
  };

  const banner = BannerComponent || getBanner();

  const [isDeactivateBuildCacheDialogOpen, setIsDeactivateBuildCacheDialogOpen] = useState(false);

  const bannerRef = useRef<HTMLDivElement>(null);
  const { height = 0 } = useResizeObserver({ ref: bannerRef });
  const aboveContentHeight = height;
  const bannerBox = banner && <Box paddingLeft={['unset', NAV_WIDTH]}>{banner}</Box>;

  return (
    <Page {...rest} bg={pageBg} paddingTop={HEADER_HEIGHT + height} paddingLeft={!isMobile ? NAV_WIDTH : undefined}>
      <Box
        background="white"
        width={`calc(100% - ${NAV_WIDTH}px)`}
        height={HEADER_HEIGHT + height}
        position="absolute"
        top="0"
      />
      <PlatformShell>
        <div ref={bannerRef}>{bannerBox}</div>
      </PlatformShell>

      {sidebarContent && isMobile && !isSidebarOpen && (
        <Box backgroundColor="neutral.100">
          <SidebarButton onClick={openSidebar} title={sidebarTitle} />
          <Divider />
        </Box>
      )}

      {sidebarContent && (!isMobile || isSidebarOpen) && (
        <Sidebar
          title={sidebarTitle}
          overflowY="auto"
          width={['100%', sidebarWidth]}
          height={`calc(100vh - ${HEADER_HEIGHT + aboveContentHeight}px)`}
          left={!isMobile ? NAV_WIDTH : '0'}
          top={`${HEADER_HEIGHT + aboveContentHeight}px`}
          position="fixed"
          backgroundColor="neutral.100"
          borderRight="1px solid"
          borderRightColor="separator.primary"
        >
          {sidebarContent}
        </Sidebar>
      )}

      {(!isMobile || !isSidebarOpen) && (
        <Box
          as="main"
          flexGrow={1}
          marginLeft={sidebarContent ? ['unset', sidebarWidth] : undefined}
          minHeight="100%"
          display="flex"
          flexDirection="column"
        >
          {children}
        </Box>
      )}

      {!withoutFooter && (
        <InternalFooter
          bg={footerBg}
          marginTop="auto"
          paddingLeft={['12', sidebarContent ? `calc(${sidebarWidth} + 12px)` : '12']}
        />
      )}
      <TimeBasedTrialDialog />
      <TrialUpdateDialog />
      {account && <PlanDowngradeDialog account={account} />}
      {account && (
        <DeactivateBuildCacheDialog
          isOpen={isDeactivateBuildCacheDialogOpen}
          workspaceSlug={account.slug}
          onActivateSelected={() => {
            setIsDeactivateBuildCacheDialogOpen(false);
            requestSubscription();
          }}
          onReasonSubmitted={() => {
            setIsDeactivateBuildCacheDialogOpen(false);
            setIsBuildCacheTrialNotificationOpen(false);
          }}
          onClose={() => {
            setIsDeactivateBuildCacheDialogOpen(false);
          }}
        />
      )}
    </Page>
  );
};

export const withProtectedPage =
  <P extends object>(WrappedComponent: ComponentType<P>, pageProps?: ProtectedPageProps): ComponentType<P> =>
  (props) => (
    <ProtectedPage {...pageProps}>
      <WrappedComponent {...props} />
    </ProtectedPage>
  );

export default ProtectedPage;
